import { useState } from 'react';
import {
  Text,
  Code,
  FormattedDateTime,
  SegmentedControl,
  CodeBlock,
  Spinner,
  Flex,
  Box,
  Divider,
} from '@customerio/pluma-components/react';
import { useQueryClient } from '@tanstack/react-query';
import type { SearchResult } from '@/services/auth/types';
import { useHydraUIAPIQuery } from '@/services/api';
import { getSnippet } from '@/services/db/snippet/queries';
import { snippetKeys } from '@/services/db/snippet/entity';
import { isEmptyString } from '@/utils/string';
import { MetadataRow } from './metadata-row';
import * as styles from './index.css';

type Props = { result: Extract<SearchResult, { kind: 'snippet' }> };

type SnippetViewMode = 'code' | 'preview';

// The search payload carries only a matched-text excerpt of a snippet (the base
// `snippets` field), not its full Liquid body — see SnippetResult in
// services/auth/types.ts. So, like the Snippets page (and template-preview here),
// we fetch the snippet by id and render its body, mirroring the page's Code
// (handlebars-highlighted source) / Preview (sandboxed HTML iframe) views.
export function SnippetPreview({ result }: Props) {
  const qc = useQueryClient();
  const envId = Number(result.other.env_id);
  const snippetId = result.other.id;
  const isFetchable = Number.isFinite(envId) && envId > 0 && !isEmptyString(snippetId);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: snippetKeys.detailWithUsage(envId, snippetId),
    queryFn: () => getSnippet(envId, snippetId, qc),
    queryConfig: { enabled: isFetchable, staleTime: 5 * 60 * 1000 },
  });

  const value = data?.snippet.value;
  const hasBody = value != null && !isEmptyString(value);
  const showBody = isFetchable && (isLoading || isError || hasBody);

  return (
    <>
      {showBody ? (
        <>
          {/* Key by snippet id so the Code/Preview toggle resets to the default
              Code view when the quick-look moves to a different snippet —
              SnippetPreview itself stays mounted across result changes. */}
          <SnippetBody key={snippetId} isLoading={isLoading} isError={isError} value={value} />
          <Divider mt="100" />
        </>
      ) : null}

      <Flex direction="column" gap="100" mt={showBody ? '100' : undefined}>
        <MetadataRow label="ID">
          <Code size="xs">{result.other.id}</Code>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
          </Text>
        </MetadataRow>
      </Flex>
    </>
  );
}

const VIEW_MODE_OPTIONS = [
  { value: 'code', label: 'Code' },
  { value: 'preview', label: 'Preview' },
];

type SnippetBodyProps = {
  isLoading: boolean;
  isError: boolean;
  value: string | undefined;
};

// Renders the snippet body in the active view. The Code/Preview toggle mirrors
// the Snippets page: "Code" shows the Liquid/HTML source via a handlebars
// <CodeBlock>; "Preview" renders the raw value in a sandboxed iframe (Liquid
// tags surface as literal text — the page never evaluates Liquid either).
function SnippetBody({ isLoading, isError, value }: SnippetBodyProps) {
  const [mode, setMode] = useState<SnippetViewMode>('code');

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="200">
        <Spinner />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Text size="xs" color="subtle">
        Failed to load preview
      </Text>
    );
  }
  if (value == null || isEmptyString(value)) return null;

  return (
    <Flex direction="column" gap="100" w="full">
      <Flex justifyContent="flex-end">
        <SegmentedControl
          ariaLabel="Snippet view mode"
          name="snippet_quick_look_view_mode"
          size="small"
          value={mode}
          onChange={(next) => setMode(next as SnippetViewMode)}
          options={VIEW_MODE_OPTIONS}
        />
      </Flex>
      {mode === 'code' ? (
        <Box className={styles.snippetCodeFrame} data-test-snippet-quick-look-code>
          <CodeBlock code={value} language="handlebars" withCopyButton flexGrow="1" />
        </Box>
      ) : (
        <Box className={styles.snippetPreviewFrame} data-test-snippet-quick-look-preview>
          <iframe className={styles.snippetPreviewIframe} srcDoc={value} sandbox="" title="Snippet preview" />
        </Box>
      )}
    </Flex>
  );
}
